import React from 'react';

import { SegmentedControl as RadixSegmentedControl } from '@radix-ui/themes';
import { useThemeContext } from '@radix-ui/themes';
import clsx from 'clsx';

export type SegmentedControlSize = 'small' | 'medium' | 'large';
export type SegmentedControlVariant = 'surface' | 'classic';
export type SegmentedControlColor =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error';
export type SegmentedControlRadius =
  | 'none'
  | 'small'
  | 'medium'
  | 'large'
  | 'full';

export interface SegmentedControlItem {
  value: string;
  label: React.ReactNode;
}

export interface SegmentedControlProps {
  items: SegmentedControlItem[];
  value: string;
  onValueChange: (value: string) => void;
  size?: SegmentedControlSize;
  variant?: SegmentedControlVariant;
  color?: SegmentedControlColor;
  radius?: SegmentedControlRadius;
  disabled?: boolean;
  className?: string;
  fullWidth?: boolean;
}

const sizeMapping: Record<SegmentedControlSize, '1' | '2' | '3'> = {
  small: '1',
  medium: '2',
  large: '3',
};

const radiusMapping: Record<
  SegmentedControlRadius,
  'none' | 'small' | 'medium' | 'large' | 'full'
> = {
  none: 'none',
  small: 'small',
  medium: 'medium',
  large: 'large',
  full: 'full',
};

function resolveColor(
  color: SegmentedControlColor,
  accentColor?: string
): string | undefined {
  switch (color) {
    case 'primary':
      return accentColor || 'cyan';
    case 'secondary':
      return 'gray';
    case 'success':
      return 'jade';
    case 'warning':
      return 'amber';
    case 'error':
      return 'crimson';
    case 'default':
    default:
      return undefined; // Use default Radix color
  }
}

/**
 * SegmentedControl — wrapper for Radix Themes SegmentedControl.
 * Use for switching between views or modes in your design system.
 * Supports size, variant, color, radius, and fullWidth props.
 */
export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  items,
  value,
  onValueChange,
  size = 'medium',
  variant = 'surface',
  color = 'default',
  radius = 'large',
  disabled = false,
  className,
  fullWidth = false,
}) => {
  const theme = useThemeContext();
  const accentColor = theme?.accentColor || 'cyan';

  const mappedSize = sizeMapping[size];
  const mappedRadius = radiusMapping[radius];
  const resolvedColor = resolveColor(color, accentColor);

  return (
    <RadixSegmentedControl.Root
      value={value}
      onValueChange={onValueChange}
      size={mappedSize}
      variant={variant}
      radius={mappedRadius}
      disabled={disabled}
      className={clsx(
        'segmented-control',
        `segmented-control--size-${size}`,
        `segmented-control--variant-${variant}`,
        `segmented-control--color-${color}`,
        `segmented-control--radius-${radius}`,
        {
          'segmented-control--fullWidth': fullWidth,
          'segmented-control--disabled': disabled,
        },
        className
      )}
      style={
        resolvedColor
          ? ({ '--accent-color': resolvedColor } as React.CSSProperties)
          : undefined
      }
    >
      {items.map(item => (
        <RadixSegmentedControl.Item key={item.value} value={item.value}>
          {item.label}
        </RadixSegmentedControl.Item>
      ))}
    </RadixSegmentedControl.Root>
  );
};

SegmentedControl.displayName = 'SegmentedControl';
